import { Button, Empty, Table, Tooltip } from "antd"
import { ColumnsType } from "antd/es/table"
import React from "react"
import { useHistory, generatePath } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { formatOptionalDate } from "Shared/utils/date.utils"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { PlusCircleOutlined } from "@ant-design/icons"
import { DeliveryNoteForLinking } from "../../deliveryNotes/types/deliveryNote.interfaces"
import { TruckIcon } from "../../deliveryNotes/icons/TruckIcon"

const columns: ColumnsType<DeliveryNoteForLinking> = [
    {
        key: "icon",
        width: 50,
        render: () => <TruckIcon width={34} height={34} />,
    },
    {
        key: "deliveryNoteNumber",
        width: 150,
        render: (_, { deliveryNoteNumber, description }) => (
            <div className="flex flex-col">
                <Tooltip title={deliveryNoteNumber}>
                    <span className="font-medium max-w-120 truncate">#{deliveryNoteNumber}</span>
                </Tooltip>
                <Tooltip title={description}>
                    <span className="text-gray-500 max-w-120 truncate">{description}</span>
                </Tooltip>
            </div>
        ),
    },
    {
        key: "dateOfIssue",
        width: 100,
        render: (_, { dateOfIssue }) => formatOptionalDate(dateOfIssue),
    },
    {
        key: "vendor",
        width: 180,
        render: (_, { vendor }) => (
            <Tooltip title={vendor?.name}>
                <span className="text-gray-700 max-w-120 truncate">{vendor?.name || "—"}</span>
            </Tooltip>
        ),
    },
]

interface LinkedDeliveryNoteSectionProps {
    purchaseOrderId?: string
    deliveryNotes?: Array<DeliveryNoteForLinking>
    onAddDeliveryNote?: () => void
    editable?: boolean
}

export const LinkedDeliveryNoteSection = ({ deliveryNotes = [], onAddDeliveryNote, editable = false }: LinkedDeliveryNoteSectionProps) => {
    const { t } = useTranslation()
    const history = useHistory()
    return (
        <>
            <div className="mb-10 flex justify-between">
                {editable && (
                    <Button type="primary" onClick={onAddDeliveryNote} className="ml-auto">
                        <PlusCircleOutlined />
                        {t("purchaseOrders:details.addDeliveryNote")}
                    </Button>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={deliveryNotes}
                rowKey="_id"
                pagination={false}
                size="middle"
                showHeader={false}
                onRow={(record) => {
                    const hasReadAccess = record?.metadata?.userAccess?.read
                    return {
                        onClick: () => {
                            if (hasReadAccess) {
                                history.push(generatePath(RoutePathEnum.DELIVERY_NOTE_DETAILS_PAGE, { id: record._id }))
                            }
                        },
                        style: {
                            cursor: hasReadAccess ? "default" : "not-allowed",
                        },
                    }
                }}
                className="w-full"
                locale={{
                    emptyText: (
                        <div className="animation-appear">
                            <Empty
                                className="animation-appear"
                                image={`./icons/empty_table.svg`}
                                imageStyle={{ height: 50, marginRight: "auto", marginLeft: "auto", display: "inline-block" }}
                                description={<span className="text-md font-medium mb-0">{t("info:no_linking_data_found.delivery_note.message")}</span>}
                            ></Empty>
                        </div>
                    ),
                }}
            />
        </>
    )
}
